import axios from "axios";
import { authorizeMe, axiosInstance } from "../../../../utils/APIs/commonHeadApiLogic";

const API_BASE_URL = "/api/admins";
const CHUNK_SIZE = 20 * 1024 * 1024; // 20MB
const MAX_RETRIES = 5;
const REQUEST_TIMEOUT = 180000;

export class S3UploadService {
  constructor(callbacks = {}) {
    this.onProgress = callbacks.onProgress || (() => {});
    this.onRetry = callbacks.onRetry || (() => {});
    this.onError = callbacks.onError || (() => {});
    this.onSuccess = callbacks.onSuccess || (() => {});
    this.isCancelled = false;
  }

  cancel() {
    this.isCancelled = true;
  }

  _getWaitTime(attempt) {
    const base = 1000 * Math.pow(2, attempt);
    return base + Math.random() * 0.2 * base;
  }

  _isRetryableError(error) {
    if (axios.isCancel(error)) return false;

    const status = error.response ? error.response.status : null;

    return !status || status >= 500 || status === 429;
  }

  async _withRetry(operation, label) {
    let lastError;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      if (this.isCancelled) throw new Error("Upload cancelled by user");

      try {
        return await operation();
      } catch (error) {
        lastError = error;

        if (!this._isRetryableError(error) || attempt === MAX_RETRIES - 1) {
          throw error;
        }

        const waitTime = this._getWaitTime(attempt);

        this.onRetry({
          filename: label,
          attempt: attempt + 1,
          maxRetries: MAX_RETRIES,
          nextRetryDelay: Math.round(waitTime),
          error: error.message,
        });

        await new Promise((resolve) => setTimeout(resolve, waitTime));
      }
    }

    throw lastError;
  }

  async upload(file, veroaBookingId, additionalData = {}) {
    const startTime = Date.now();
    const uploadStateKey = `s3_upload_${veroaBookingId}_${file.name}_${file.size}`;

    try {
      authorizeMe();

      const initResponse = await this._withRetry(async () => {
        const res = await axiosInstance.post(
          `${API_BASE_URL}/start`,
          {
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            veroaBookingId,
            ...additionalData, // Pass category, etc.
          },
          { timeout: REQUEST_TIMEOUT }
        );

        return res.data;
      }, "Init Upload");

      const { strategy, key, uploadId, uploadUrl } = initResponse;

      if (strategy === "single") {
        await this._withRetry(async () => {
          await axios.put(uploadUrl, file, {
            headers: { "Content-Type": file.type },
            onUploadProgress: (progressEvent) => {
              const percent = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );

              this.onProgress({
                percent,
                fileName: file.name,
              });
            },
          });
        }, file.name);

        authorizeMe();

        const finalRes = await axiosInstance.post(
          `${API_BASE_URL}/complete`,
          {
            key,
            veroaBookingId,
            parts: [],
            ...additionalData, // Pass category, clientId, photographerId, etc.
          },
          { timeout: REQUEST_TIMEOUT }
        );

        this.onSuccess({
          key,
          metrics: { duration: Date.now() - startTime },
        });

        return finalRes.data;
      } else {
        const totalParts = Math.ceil(file.size / CHUNK_SIZE);

        let uploadedParts = JSON.parse(
          localStorage.getItem(uploadStateKey) || "[]"
        );

        console.log(
          `Resuming multipart upload ${uploadedParts.length}/${totalParts}`
        );

        for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
          if (this.isCancelled) throw new Error("Upload cancelled by user");

          if (uploadedParts.some((p) => p.PartNumber === partNumber)) continue;

          const start = (partNumber - 1) * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);

          authorizeMe();

          const partUrlData = await this._withRetry(async () => {
            const res = await axiosInstance.post(
              `${API_BASE_URL}/get-part-url`,
              {
                key,
                uploadId,
                partNumber,
              },
              { timeout: REQUEST_TIMEOUT }
            );

            return res.data;
          }, `Part ${partNumber} URL`);

          const etag = await this._withRetry(async () => {
            const res = await axios.put(partUrlData.uploadUrl, chunk, {
              headers: { "Content-Type": file.type },
            });

            return res.headers.etag;
          }, `Part ${partNumber} Upload`);

          uploadedParts.push({
            ETag: etag,
            PartNumber: partNumber,
          });

          localStorage.setItem(uploadStateKey, JSON.stringify(uploadedParts));

          const percent = Math.round(
            (uploadedParts.length * 100) / totalParts
          );

          this.onProgress({
            percent,
            fileName: file.name,
            part: partNumber,
            totalParts,
          });
        }

        authorizeMe();

        const finalRes = await this._withRetry(async () => {
          const res = await axiosInstance.post(
            `${API_BASE_URL}/complete`,
            {
              key,
              uploadId,
              parts: uploadedParts,
              veroaBookingId,
              ...additionalData, // Pass category, clientId, photographerId, etc.
            },
            { timeout: REQUEST_TIMEOUT }
          );

          return res.data;
        }, "Complete Upload");

        localStorage.removeItem(uploadStateKey);

        this.onSuccess({
          key,
          metrics: { duration: Date.now() - startTime },
        });

        return finalRes;
      }
    } catch (error) {
      console.error("Upload failed:", error);
      this.onError(error);
      throw error;
    }
  }
}